/**
 * Bereitet die kanonischen Storno-Felder einer Fahrt für die Anzeige auf
 * (cancelledByRole / cancellationReasonKey aus Phase 1 des Benachrichtigungs-
 * Konzepts, siehe docs/BENACHRICHTIGUNGEN_KONZEPT.md). Das Mapping entspricht
 * classifyCancellationForPortal() in den Cloud Functions; Fahrten von
 * Alt-App-Versionen fallen auf cancelReason/cancellationHistory zurück.
 *
 * Rückgabe: { by, reason } (beides optional) oder null, wenn die Fahrt nicht
 * storniert ist. `by` = wer storniert hat, `reason` = warum.
 */

const PORTAL_ROLES = new Set([
  'admin',
  'super_admin',
  'central_admin',
  'dispatcher',
  'central_dispatch',
  'operator_admin',
  'operator_staff'
]);

// Stornogrund-Keys des Fahrer-Dialogs (home_driver.dart)
const DRIVER_REASON_LABELS = {
  fahrgast_nicht_vor_ort: 'Fahrgast nicht angetroffen',
  fahrgast_bittet_stornierung: 'Auf Wunsch des Fahrgasts',
  schaffe_es_nicht: 'Schaffe es nicht rechtzeitig',
  versehentlich_angenommen: 'Versehentlich angenommen',
  walk_in_not_started: 'Einsteiger-Tour nicht zustande gekommen',
  walk_in_start_failed: 'Einsteiger-Tour: Start fehlgeschlagen'
};

const CANCELLED_STATUSES = ['cancelled', 'cancelled_by_customer', 'cancelled_by_driver'];

const lastCancellationHistoryEntry = (ride) => {
  const history = ride?.cancellationHistory;
  if (!Array.isArray(history) || history.length === 0) return null;
  const entry = history[history.length - 1];
  return entry && typeof entry === 'object' ? entry : null;
};

export const getCancellationInfo = (ride) => {
  if (!ride || !CANCELLED_STATUSES.includes(ride.status)) return null;

  const reasonKey = String(ride.cancellationReasonKey || '');
  const legacyReason = String(ride.cancellationReason || ride.cancelReason || '');
  const cancelledByRole = String(ride.cancelledByRole || '');

  // Fahrer storniert endgültig: Grund-Key steckt im letzten History-Eintrag
  // (Top-Level gibt es bei älteren App-Versionen nur das deutsche Label)
  if (ride.status === 'cancelled_by_driver') {
    const historyEntry = lastCancellationHistoryEntry(ride);
    const driverReasonKey = String(historyEntry?.reasonKey || ride.cancelReasonKey || '');
    const driverName = historyEntry?.driverName || ride.driverName || '';
    return {
      by: driverName ? `Fahrer ${driverName}` : 'Fahrer',
      reason: DRIVER_REASON_LABELS[driverReasonKey] || legacyReason || historyEntry?.reason || null
    };
  }

  // Automatischer Timeout-Storno: kein Fahrer gefunden
  if (reasonKey === 'auto_timeout' || legacyReason === 'auto_timeout') {
    const timeoutMinutes = typeof ride.timeoutMinutes === 'number' ? ride.timeoutMinutes : null;
    return {
      by: 'System',
      reason: `Kein Fahrer gefunden${timeoutMinutes ? ` (Timeout nach ${timeoutMinutes} Min)` : ' (Timeout)'}`
    };
  }

  // Vorbestellung storniert, weil der Gegenvorschlag abgelaufen ist
  if (
    reasonKey === 'gegenvorschlag_expired' ||
    legacyReason === 'Gegenvorschlag abgelaufen' ||
    String(ride.gegenvorschlagStatus || '') === 'expired'
  ) {
    return { by: 'System', reason: 'Gegenvorschlag abgelaufen' };
  }

  if (cancelledByRole === 'ai_telephonist' || legacyReason === 'Kundenstorno (KI)') {
    return { by: 'Kunde (Telefon-KI)', reason: null };
  }

  if (
    cancelledByRole === 'customer' ||
    (ride.customerId && (ride.cancelledBy === ride.customerId || ride.cancelledByUserId === ride.customerId))
  ) {
    return { by: 'Kunde (App)', reason: null };
  }

  if (PORTAL_ROLES.has(cancelledByRole)) {
    return { by: 'Zentrale', reason: legacyReason || null };
  }

  // Alt-App ohne Rollen-Felder: nur den Freitext-Grund zeigen, nichts raten
  return legacyReason ? { by: null, reason: legacyReason } : null;
};

/** Einzeiler für kompakte Anzeigen (z. B. unter dem Status-Chip der Fahrtenliste). */
export const getCancellationShortLabel = (ride) => {
  const info = getCancellationInfo(ride);
  if (!info) return null;
  if (info.by && info.reason) return `${info.by}: ${info.reason}`;
  return info.by || info.reason || null;
};
